import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const ProbateConsequencesForm = ({
  formId,
  title,
  subtitle,
}: {
  formId: string;
  title: string;
  subtitle: string;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    timeline: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('situation', formData.situation);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d678q65t1k61917cl9rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">
          We&apos;ve received your information and will contact you within 24
          hours to discuss your property situation.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property Address
          </label>
          <input
            type="text"
            name="propertyAddress"
            value={formData.propertyAddress}
            onChange={(e) =>
              setFormData({ ...formData, propertyAddress: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Address of the property"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Situation
            </label>
            <select
              name="situation"
              value={formData.situation}
              onChange={(e) =>
                setFormData({ ...formData, situation: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select situation</option>
              <option value="Will Not Probated">Will was never probated</option>
              <option value="Past 4 Year Deadline">Past the 4-year deadline</option>
              <option value="Title Issues">Having title issues</option>
              <option value="Want to Sell Property">Want to sell inherited property</option>
              <option value="Multiple Heirs">Multiple heirs involved</option>
              <option value="No Will Exists">No will exists</option>
              <option value="Other">Other situation</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeline
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={(e) =>
                setFormData({ ...formData, timeline: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="ASAP">Need help ASAP</option>
              <option value="1-3 months">Within 1-3 months</option>
              <option value="3-6 months">Within 3-6 months</option>
              <option value="6+ months">6+ months</option>
              <option value="Just exploring">Just exploring options</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tell Us More
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e.target.value.slice(0, 500),
              })
            }
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Tell us about your situation and how we can help..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.message.length}/500 characters
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get Your Free Consultation'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be sold. We&apos;re here to
          help.
        </p>
      </form>
    </div>
  );
};

export default function WhatHappensIfYouDontProbateWillTexasPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="What Happens If You Don't Probate a Will in Texas | Consequences & Solutions | TREX RE LLC"
        description="Learn the serious consequences of not probating a will in Texas. Understand intestacy laws, property title issues, loss of executor authority, and your options if you've missed the 4-year deadline."
        keywords="not probating will Texas, consequences no probate Texas, Texas intestacy laws, property title issues probate, 4 year probate deadline Texas, executor authority Texas, El Paso probate help"
        canonical="/elpaso-tx/what-happens-if-you-dont-probate-will-texas"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'What Happens If You Don\'t Probate a Will in Texas',
          description: 'Comprehensive guide explaining the consequences of not probating a will in Texas, including intestacy laws, title issues, and available solutions.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/6b418bd5c9d2a119d53daf7eb8f518a7.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-red-500/90 text-white text-sm font-semibold rounded-full mb-6">
                <i className="ri-error-warning-line mr-2"></i>
                Important Legal Information
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                What Happens If You{' '}
                <span className="text-teal-400">Don&apos;t Probate</span> a Will in Texas?
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Failing to probate a will can have serious consequences for your family and inherited property. Learn what happens, why it matters, and what options you still have.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-alert-line text-red-400 mr-2"></i>
                  <span>Title Transfer Issues</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-alert-line text-red-400 mr-2"></i>
                  <span>Intestacy Laws Apply</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-alert-line text-red-400 mr-2"></i>
                  <span>Lost Executor Authority</span>
                </div>
              </div>
            </div>

            <div>
              <ProbateConsequencesForm
                formId="hero-no-probate-form"
                title="Need Help With an Unprobated Estate?"
                subtitle="We buy inherited properties even with title issues"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When a loved one passes away, dealing with legal paperwork is often the last thing on anyone&apos;s mind. Grief, funeral arrangements, and family matters take priority — and understandably so. But here&apos;s something many Texas families don&apos;t realize until it&apos;s too late: <strong>failing to probate a will can create serious, long-lasting problems</strong> that affect your ability to sell property, access bank accounts, and honor your loved one&apos;s final wishes.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Maybe you found a will but assumed everything would just &quot;work itself out.&quot; Perhaps you didn&apos;t know there was a deadline. Or maybe the estate seemed simple enough that probate felt unnecessary. Whatever the reason, if you&apos;re now facing the consequences of an unprobated will, you&apos;re not alone — and there may still be options available to you.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            In this comprehensive guide, we&apos;ll explain exactly what happens when you don&apos;t probate a will in Texas, the specific consequences you may face, and what solutions exist for families dealing with this situation. At TREX RE LLC, we&apos;ve helped many El Paso families navigate complicated inheritance situations, including properties with title issues stemming from unprobated estates. Understanding your options is the first step toward finding a solution.
          </p>
        </div>

        {/* The Four-Year Rule Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Critical Four-Year Deadline You Need to Know
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=calendar%20with%20red%20circle%20marking%20important%20deadline%20date%20hourglass%20running%20out%20of%20time%20legal%20documents%20on%20desk%20urgent%20time%20sensitive%20concept%20professional%20business%20photography%20warm%20lighting&width=800&height=500&seq=no-probate-deadline-002&orientation=landscape"
            alt="Calendar showing critical probate deadline"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Texas law is very specific about probate timing: <strong>a will must generally be filed for probate within four years of the date of death</strong>. This isn&apos;t just a suggestion or a guideline — it&apos;s a legal requirement with real consequences if you miss it.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Why does this deadline exist? The legal system needs finality. After four years, memories fade, witnesses become harder to locate, and the potential for fraud increases. The deadline protects everyone involved by ensuring estates are settled in a reasonable timeframe.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            But here&apos;s what catches many families off guard: <strong>the four-year clock starts ticking from the date of death, not from when you found the will or when you decided to deal with the estate</strong>. If your grandmother passed away five years ago and you just discovered her will in a safety deposit box, you&apos;ve already missed the standard deadline.
          </p>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-error-warning-line mr-2 text-red-500"></i>
              Critical Warning
            </h4>
            <p className="text-gray-700">
              If you&apos;re approaching the four-year mark, act immediately. Every day that passes makes your situation more complicated. Even if you&apos;re past the deadline, there may still be options — but they require prompt action and often legal assistance.
            </p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            There is one potential exception: if you can prove to the court that you weren&apos;t &quot;in default&quot; for failing to present the will within four years, the court may still accept it. This means demonstrating you had a valid reason for the delay — perhaps you didn&apos;t know the will existed, or you were incapacitated. However, this requires additional legal proceedings and isn&apos;t guaranteed to succeed. For a complete understanding of the{' '}
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              Texas probate process
            </a>
            , including timelines and procedures, see our comprehensive guide.
          </p>
        </section>

        {/* Consequence 1: Intestacy Laws */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Consequence #1: Texas Intestacy Laws Take Over
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Perhaps the most significant consequence of not probating a will is that <strong>Texas law treats the estate as if no will ever existed</strong>. This is called &quot;intestate succession,&quot; and it means the state&apos;s default rules determine who inherits — regardless of what the deceased person actually wanted.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Think about that for a moment. Your loved one took the time to create a will, carefully deciding who should receive their property, who should be excluded, and how their legacy should be distributed. Without probate, all of that planning becomes meaningless. The will might as well not exist.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=family%20tree%20diagram%20showing%20inheritance%20distribution%20with%20legal%20documents%20texas%20state%20flag%20in%20background%20estate%20planning%20concept%20professional%20illustration%20style%20clean%20modern%20design&width=800&height=500&seq=no-probate-intestacy-003&orientation=landscape"
            alt="Family inheritance distribution under intestacy laws"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            How Texas Intestacy Laws Distribute Assets
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Under Texas intestacy laws, assets are distributed to &quot;heirs-at-law&quot; — typically the surviving spouse and children — according to a specific formula. This can lead to outcomes the deceased never intended:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                Scenario: Blended Families
              </h4>
              <p className="text-gray-700 text-sm">
                If the deceased had children from a previous relationship, the surviving spouse may receive far less than intended. The deceased&apos;s share of community property goes to the children, not the spouse — even if the will said otherwise.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                Scenario: Estranged Family Members
              </h4>
              <p className="text-gray-700 text-sm">
                Perhaps the deceased intentionally excluded a child from their will due to estrangement. Under intestacy, that child becomes an heir-at-law and is entitled to a share of the estate — directly contradicting the deceased&apos;s wishes.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                Scenario: Non-Family Beneficiaries
              </h4>
              <p className="text-gray-700 text-sm">
                If the will left property to a close friend, charity, or caregiver who isn&apos;t a legal relative, they receive nothing under intestacy. Only blood relatives and legal spouses inherit under Texas intestate succession.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                Scenario: Unmarried Partners
              </h4>
              <p className="text-gray-700 text-sm">
                Long-term partners who aren&apos;t legally married have no inheritance rights under Texas intestacy laws. Even if they lived together for decades and the will named them as the primary beneficiary, they&apos;re legally entitled to nothing.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The emotional impact of intestacy can be devastating. Family members who were supposed to inherit may receive nothing, while others who were intentionally excluded may receive substantial assets. This often leads to family conflict, resentment, and legal disputes that could have been avoided by simply probating the will on time.
          </p>
        </section>

        {/* Consequence 2: Property Title Issues */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Consequence #2: Property Title Issues Make Selling Nearly Impossible
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For many families, the most immediate and frustrating consequence of not probating a will involves real estate. <strong>Without probate, you cannot legally transfer ownership of real property</strong>. The deceased person&apos;s name remains on the deed, and no one has the legal authority to sell, refinance, or even properly maintain the property.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This creates what&apos;s known as a &quot;cloud on title&quot; — a legal defect that prevents clear ownership transfer. Title companies won&apos;t insure the property, banks won&apos;t provide mortgages to buyers, and most buyers won&apos;t purchase a property with title issues. The house essentially becomes stuck in legal limbo.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=house%20with%20for%20sale%20sign%20and%20red%20warning%20tape%20showing%20property%20with%20legal%20issues%20title%20problems%20real%20estate%20complications%20southwestern%20style%20home%20in%20texas%20professional%20photography&width=800&height=500&seq=no-probate-title-004&orientation=landscape"
            alt="Property with title issues preventing sale"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Real-World Problems Families Face
          </h3>

          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mr-4 flex-shrink-0">
                <i className="ri-close-line text-red-600"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Can&apos;t Sell the Property</p>
                <p className="text-gray-600 text-sm">
                  Without clear title, traditional buyers and their lenders won&apos;t touch the property. You may have interested buyers, but the sale can&apos;t close.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mr-4 flex-shrink-0">
                <i className="ri-close-line text-red-600"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Can&apos;t Refinance or Get Home Equity</p>
                <p className="text-gray-600 text-sm">
                  Banks require clear title to issue loans. If you need to access equity for repairs or other expenses, you&apos;re out of luck.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mr-4 flex-shrink-0">
                <i className="ri-close-line text-red-600"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Property Taxes and Maintenance Continue</p>
                <p className="text-gray-600 text-sm">
                  Even though you can&apos;t sell, property taxes still come due. The house still needs maintenance. These costs add up while the property sits in limbo.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mr-4 flex-shrink-0">
                <i className="ri-close-line text-red-600"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Insurance Complications</p>
                <p className="text-gray-600 text-sm">
                  Homeowner&apos;s insurance policies are tied to ownership. Without clear title, maintaining proper insurance coverage becomes complicated.
                </p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            We&apos;ve seen families struggle with these{' '}
            <a
              href="/title-issues"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              title issues
            </a>{' '}
            for years, watching property values fluctuate while they&apos;re unable to act. The longer the situation continues, the more complicated it becomes — especially if additional family members pass away, creating multiple generations of unclear ownership.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
              There May Be a Solution
            </h4>
            <p className="text-gray-700">
              Even with title issues from an unprobated will, selling may still be possible. Cash buyers like TREX RE LLC specialize in purchasing properties with complicated title situations. We work with title companies and attorneys to find solutions that allow families to finally move forward.
            </p>
          </div>
        </section>

        {/* Consequence 3: Loss of Executor Authority */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Consequence #3: The Named Executor Has No Legal Authority
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When someone creates a will, they typically name an executor — a trusted person responsible for carrying out their final wishes. But here&apos;s what many people don&apos;t understand: <strong>being named executor in a will doesn&apos;t automatically give you any legal authority</strong>. That authority only comes through the probate process.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Without probate, the named executor cannot legally act on behalf of the estate. They can&apos;t access bank accounts, pay debts, collect money owed to the estate, transfer property, or distribute assets to beneficiaries. The will names them as executor, but without &quot;Letters Testamentary&quot; issued by the court, they have no more legal authority than any other family member.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=frustrated%20person%20at%20bank%20counter%20being%20denied%20access%20to%20account%20showing%20identification%20documents%20bank%20teller%20explaining%20policy%20professional%20business%20photography%20warm%20lighting&width=800&height=500&seq=no-probate-executor-005&orientation=landscape"
            alt="Executor being denied access without legal authority"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            What the Executor Cannot Do Without Probate
          </h3>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mb-3">
                <i className="ri-bank-line text-red-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Access Bank Accounts</h4>
              <p className="text-gray-600 text-sm">
                Banks require Letters Testamentary before releasing funds. Without them, accounts remain frozen.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mb-3">
                <i className="ri-money-dollar-circle-line text-red-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Pay Estate Debts</h4>
              <p className="text-gray-600 text-sm">
                Creditors may continue collection efforts, but the executor has no authority to settle debts from estate funds.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mb-3">
                <i className="ri-file-transfer-line text-red-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Transfer Property Titles</h4>
              <p className="text-gray-600 text-sm">
                Real estate, vehicles, and other titled property cannot be legally transferred to beneficiaries.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mb-3">
                <i className="ri-group-line text-red-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Distribute to Beneficiaries</h4>
              <p className="text-gray-600 text-sm">
                Even if everyone agrees on distribution, the executor has no legal authority to make it happen.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This creates a frustrating situation where the person the deceased trusted most to handle their affairs is legally powerless to do so. Family members may expect the executor to &quot;just handle things,&quot; not understanding that without probate, their hands are tied.
          </p>
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  Dealing With an Unprobated Estate?
                </h2>
                <p className="text-gray-300 mb-6">
                  We specialize in helping families sell inherited properties — even when probate was never completed. Get a fair cash offer with no obligation.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>We handle title complications</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Buy properties in any condition</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Work with multiple heirs</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Fast, fair cash offers</span>
                  </li>
                </ul>
              </div>
              <div>
                <ProbateConsequencesForm
                  formId="middle-no-probate-form"
                  title="Get Your Cash Offer"
                  subtitle="No obligation, no pressure"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Consequence 4: Surrender Requirement */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Consequence #4: You Must Still Surrender the Will
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Here&apos;s something that surprises many people: <strong>even if you decide not to probate a will, you&apos;re still legally required to surrender it to the court</strong>. Texas law mandates that anyone in possession of a deceased person&apos;s will must deliver it to the county clerk&apos;s office.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This isn&apos;t optional. Failing to surrender a will can result in legal consequences, including being held liable for damages caused by the delay. The court clerk will file the will and may notify beneficiaries named in it, even if no one petitions for probate.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Why does this matter? Because surrendering the will creates a public record that it exists. Beneficiaries who learn about the will may decide to pursue probate themselves, or they may use the information in other legal proceedings. You can&apos;t simply hide a will and pretend it doesn&apos;t exist — that&apos;s both illegal and unethical.
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-information-line mr-2 text-amber-500"></i>
              Important Note
            </h4>
            <p className="text-gray-700">
              If you&apos;re holding onto a will because you&apos;re unsure what to do, consult with a probate attorney. They can advise you on your obligations and options. Keeping a will hidden can expose you to legal liability and family conflict.
            </p>
          </div>
        </section>

        {/* Exception: Assets That Don't Require Probate */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Exception: Assets That Pass Outside of Probate
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Not all assets require probate to transfer to beneficiaries. Understanding which assets pass automatically can help you assess your situation and determine what actually needs to go through the probate process.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=financial%20planning%20documents%20showing%20life%20insurance%20policy%20retirement%20account%20statements%20beneficiary%20designation%20forms%20on%20clean%20desk%20with%20calculator%20professional%20business%20photography&width=800&height=500&seq=no-probate-exception-006&orientation=landscape"
            alt="Financial documents showing beneficiary designations"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Assets That Typically Bypass Probate
          </h3>

          <div className="space-y-4 mb-8">
            <div className="bg-teal-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                Life Insurance with Named Beneficiaries
              </h4>
              <p className="text-gray-700 text-sm">
                Life insurance proceeds go directly to the named beneficiary, bypassing the estate entirely. The beneficiary simply files a claim with the insurance company.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                Retirement Accounts (401k, IRA) with Beneficiaries
              </h4>
              <p className="text-gray-700 text-sm">
                Retirement accounts with designated beneficiaries transfer directly. The beneficiary contacts the account custodian to claim the funds.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                Payable-on-Death (POD) Bank Accounts
              </h4>
              <p className="text-gray-700 text-sm">
                Bank accounts with POD designations transfer to the named person upon death. They simply present a death certificate to the bank.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                Joint Tenancy with Right of Survivorship
              </h4>
              <p className="text-gray-700 text-sm">
                Property owned as joint tenants with right of survivorship automatically passes to the surviving owner(s) without probate.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                Transfer-on-Death Deeds
              </h4>
              <p className="text-gray-700 text-sm">
                Texas allows transfer-on-death deeds for real property. If one was properly recorded, the property transfers to the named beneficiary without probate.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-shield-check-line text-teal-600 mr-2"></i>
                Assets Held in Trust
              </h4>
              <p className="text-gray-700 text-sm">
                Property held in a living trust passes according to the trust terms, managed by the successor trustee without court involvement.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If the deceased person&apos;s major assets fall into these categories, probate may be less critical. However, if there&apos;s real estate without a transfer-on-death deed, vehicles, or other titled property, probate (or an alternative like Muniment of Title) is typically necessary to transfer ownership.
          </p>
        </section>

        {/* What Are Your Options Now Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Are Your Options If You Haven&apos;t Probated a Will?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re reading this because you&apos;re already past the four-year deadline or facing complications from an unprobated will, don&apos;t despair. While the situation is more complicated than it would have been with timely probate, options may still exist.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Option 1: Late Probate (If Within Four Years)
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re still within the four-year window, the solution is straightforward: file for probate now. Yes, you&apos;ve lost time, but you haven&apos;t lost your rights. Contact a probate attorney and get the process started immediately.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Option 2: Prove You Weren&apos;t &quot;In Default&quot;
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re past four years but can demonstrate a valid reason for the delay, the court may still accept the will. This requires filing additional documentation and potentially a hearing. Valid reasons might include not knowing the will existed, being incapacitated, or other circumstances beyond your control.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Option 3: Affidavit of Heirship
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For real property where probate is no longer possible, an Affidavit of Heirship may help establish ownership. This document, signed by someone with knowledge of the family history (not an heir), identifies the legal heirs and can be recorded in property records. While not as strong as a court order, it may allow title companies to insure the property.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Option 4: Sell to a Cash Buyer
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For families who need to sell inherited property but face title complications from an unprobated will, selling to a{' '}
            <a
              href="/elpaso-tx/cash-home-buyers-el-paso"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              cash home buyer
            </a>{' '}
            may be the most practical solution. Cash buyers like TREX RE LLC specialize in purchasing properties with complicated title situations. We work with title companies and attorneys to find creative solutions that allow families to finally move forward.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This option is particularly valuable when multiple heirs are involved, the property needs repairs, or the family simply wants to resolve the situation quickly. We handle the complications so you don&apos;t have to.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Option 5: Quiet Title Action
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In some cases, a &quot;quiet title&quot; lawsuit may be necessary to establish clear ownership. This legal action asks the court to determine who owns the property and eliminate competing claims. It&apos;s more expensive and time-consuming than other options but may be necessary for complicated situations.
          </p>
        </section>

        {/* Preventing Future Problems Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Preventing Future Problems: Lessons Learned
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re dealing with the consequences of an unprobated will, you understand firsthand how important proper estate planning and timely action are. Here are key lessons to prevent similar problems in the future:
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">1</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Create a Will and Keep It Updated</p>
                <p className="text-gray-600 text-sm">
                  If you don&apos;t have a will, create one. If you have one, review it periodically and update it after major life changes.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">2</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Tell Someone Where Your Will Is</p>
                <p className="text-gray-600 text-sm">
                  A will that can&apos;t be found is almost as bad as no will at all. Make sure your executor knows where to find it.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">3</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Use Beneficiary Designations</p>
                <p className="text-gray-600 text-sm">
                  For accounts that allow it, name beneficiaries directly. This ensures assets pass without probate complications.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">4</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Consider a Transfer-on-Death Deed</p>
                <p className="text-gray-600 text-sm">
                  For real property, a TOD deed can transfer ownership without probate while allowing you to retain full control during your lifetime.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mr-4 flex-shrink-0">
                <span className="text-teal-600 font-bold">5</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Act Promptly When Someone Passes</p>
                <p className="text-gray-600 text-sm">
                  Even if you&apos;re not ready to deal with the estate, at least file the will with the court to preserve your rights.
                </p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For families dealing with{' '}
            <a
              href="/inherited-homes"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              inherited homes
            </a>
            , understanding these principles can prevent years of frustration and legal complications. The time and money spent on proper planning is far less than the cost of dealing with problems later.
          </p>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I just ignore the will and divide things among family members?
              </h3>
              <p className="text-gray-700">
                You can informally divide personal property, but titled assets (real estate, vehicles, bank accounts) cannot be legally transferred without proper legal process. Informal agreements also have no legal protection if disputes arise later.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if all the heirs agree on how to divide the estate?
              </h3>
              <p className="text-gray-700">
                Agreement among heirs is helpful but doesn&apos;t eliminate the need for legal process to transfer titled property. You may be able to use simplified procedures like Muniment of Title, but some legal action is typically required.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Is it too late if more than four years have passed?
              </h3>
              <p className="text-gray-700">
                It&apos;s more complicated but not necessarily hopeless. You may be able to prove you weren&apos;t &quot;in default,&quot; use an Affidavit of Heirship, or pursue other legal remedies. Consult with a probate attorney to understand your specific options.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I sell inherited property without probating the will?
              </h3>
              <p className="text-gray-700">
                Traditional sales typically require clear title, which usually means probate. However, cash buyers who specialize in complicated situations may be able to purchase the property and handle title issues as part of the transaction.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What happens to the deceased person&apos;s debts?
              </h3>
              <p className="text-gray-700">
                Debts don&apos;t disappear just because probate wasn&apos;t filed. Creditors may still pursue collection, and debts secured by property (like mortgages) can lead to{' '}
                <a
                  href="/avoid-foreclosure"
                  className="text-teal-600 hover:text-teal-700"
                >
                  foreclosure
                </a>
                . However, heirs generally aren&apos;t personally responsible for unsecured debts unless they co-signed.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Do I need a lawyer to probate a will in Texas?
              </h3>
              <p className="text-gray-700">
                While not legally required, having an attorney is strongly recommended. Probate involves legal procedures, deadlines, and potential pitfalls. The cost of legal help is usually far less than the cost of mistakes.
              </p>
            </div>
          </div>
        </section>

        {/* Local Resources Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            El Paso Resources for Probate and Estate Issues
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;re dealing with probate issues in El Paso, several local resources may help:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">El Paso County Probate Court</h4>
              <p className="text-gray-600 text-sm mb-2">
                500 E. San Antonio Ave., El Paso, TX 79901
              </p>
              <p className="text-gray-600 text-sm">
                Handles all probate matters for El Paso County residents.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">El Paso Bar Association</h4>
              <p className="text-gray-600 text-sm mb-2">
                Lawyer referral service available
              </p>
              <p className="text-gray-600 text-sm">
                Can help you find a probate attorney in the El Paso area.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <a href="/elpaso-tx/epcad" className="text-teal-600 hover:text-teal-700">
                  El Paso Central Appraisal District (EPCAD)
                </a>
              </h4>
              <p className="text-gray-600 text-sm">
                For property tax information and ownership records on inherited property.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <a href="/local-news-el-paso-texas" className="text-teal-600 hover:text-teal-700">
                  El Paso Local News & Resources
                </a>
              </h4>
              <p className="text-gray-600 text-sm">
                Stay informed about local real estate market conditions and community resources.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-hand-heart-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                We&apos;re Here to Help You Move Forward
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Dealing with an unprobated estate is stressful and complicated. If you&apos;ve inherited property and need to sell — regardless of title issues or probate status — we can help find a solution.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <ProbateConsequencesForm
                formId="final-no-probate-form"
                title="Get Your Free Consultation"
                subtitle="No obligation, compassionate service"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">Prefer to talk? Call us directly:</p>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line mr-2"></i>(915) 206-5814
              </a>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/elpaso-tx/texas-intestacy-laws"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-scales-3-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Texas Intestacy Laws Explained</p>
                <p className="text-sm text-gray-600">
                  How assets are distributed without a will
                </p>
              </div>
            </a>
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-scales-3-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Texas Probate Guide</p>
                <p className="text-sm text-gray-600">
                  Complete guide to the probate process
                </p>
              </div>
            </a>
            <a
              href="/inherited-homes"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-heart-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Selling Inherited Homes</p>
                <p className="text-sm text-gray-600">
                  Options for inherited property
                </p>
              </div>
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Get Cash Offer</p>
                <p className="text-sm text-gray-600">
                  Request your no-obligation offer
                </p>
              </div>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
